<template>
  <div :class="customClass">
    <HeaderPieChart />
    <div class="grid grid-cols-12 gap-6 items-start min-h-[300px]">
      <div class="col-span-4">
        <div
          ref="chartDiv"
          :style="customStyle"
          class="p-2 text-text-secondary font-medium dark:text-dark-secondary"
        ></div>
      </div>
      <div class="col-span-8">
        <TablePieChart :data="data" :typeChart="typeChart" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref, computed, watch } from "vue";
import { getColorClass } from "../../utils/validation";
import { useThemeStore } from "../../stores/index";
import { defineProps } from "vue";

const props = defineProps({
  customStyle: {
    type: Object,
    default: () => ({
      width: "500px",
      height: "226px",
    }),
  },
  customClass: {
    type: String,
    default: "",
  },
  data: {
    type: Array,
    default: () => [],
  },
  valueField: {
    type: String,
    default: "valueClp",
  },
  categoryField: {
    type: String,
    default: "name",
  },
  seriesName: {
    type: String,
    default: "Series",
  },
  animated: {
    type: Boolean,
    default: false,
  },
  legend: {
    type: Boolean,
    default: false,
  },
  total: {
    type: Number,
    default: 0,
  },
  typeChart: {
    type: Number,
    required: true,
  },
});

const tstore = useThemeStore();

const processedData = computed(() =>
  props.data.map((item) => ({
    value: item.value || 0,
  })),
);

const isLightMode = computed(() => tstore.currentTheme === "light");
const computedStyle = computed(() => ({ ...props.customStyle }));
const chartDiv = ref(null);
let root = null;
let series = null;
let legend = null;
let chart = null;

const am5Modules = ref(null);

async function loadAmcharts() {
  try {
    const [am5, am5percent, am5themes_Animated] = await Promise.all([
      import("@amcharts/amcharts5"),
      import("@amcharts/amcharts5/percent"),
      import("@amcharts/amcharts5/themes/Animated"),
    ]);
    am5Modules.value = {
      am5,
      am5percent,
      am5themes_Animated: am5themes_Animated.default || am5themes_Animated,
    };
    return true;
  } catch (e) {
    console.warn(
      "amcharts5 no está disponible. El gráfico circular no se cargará.",
      e,
    );
    return false;
  }
}

function createRoot(element) {
  const { am5, am5themes_Animated } = am5Modules.value;
  root = am5.Root.new(element);
  if (root._logo) root._logo.dispose();
  root.setThemes([am5themes_Animated.new(root)]);
}

function createChart() {
  const { am5, am5percent } = am5Modules.value;
  return root.container.children.push(
    am5percent.PieChart.new(root, {
      radius: am5.percent(80),
      layout: root.horizontalLayout,
    }),
  );
}

function createSeries(chart) {
  const { am5percent } = am5Modules.value;
  series = chart.series.push(
    am5percent.PieSeries.new(root, {
      name: props.seriesName,
      valueField: "percentage",
      categoryField: props.categoryField,
    }),
  );

  series.slices.template.setAll({
    templateField: "sliceSettings",
  });

  updateSeriesData();

  series.labels.template.set("forceHidden", true);
  series.ticks.template.set("forceHidden", true);

  return series;
}

function updateSeriesData() {
  if (!series) return;
  const roundedData = props.data.map((item) => {
    let value = item[props.valueField] || 0;
    let total = props.total;
    let percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
    return { ...item, percentage: parseFloat(percentage) };
  });
  series.data.setAll(roundedData);
}

function addLegend(chart, series) {
  const { am5 } = am5Modules.value;
  legend = chart.children.push(
    am5.Legend.new(root, {
      centerY: am5.percent(45),
      y: am5.percent(50),
      layout: root.verticalLayout,
    }),
  );

  legend.labels.template.setAll({
    fontSize: "16px",
    textAlign: "left",
    width: am5.percent(50),
    oversizedBehavior: "wrap",
    fill: isLightMode.value ? am5.color("#4F6772") : am5.color("#D7DDE2"),
    fontWeight: 500,
    paddingBottom: 5,
    class: "dark:text-red-500",
  });

  legend.valueLabels.template.setAll({
    fontSize: "16px",
    textAlign: "right",
    width: am5.percent(1),
    fill: isLightMode.value ? am5.color("#4F6772") : am5.color("#D7DDE2"),
    fontWeight: 500,
    paddingBottom: 5,
    className: "dark:text-red-500",
  });

  legend.markerRectangles.template.setAll({
    width: 11,
    height: 11,
    cornerRadiusTL: 2,
    cornerRadiusTR: 2,
    cornerRadiusBL: 2,
    cornerRadiusBR: 2,
  });

  legend.itemContainers.template.setAll({
    paddingBottom: 10,
    minHeight: 5,
  });

  // legend.data.setAll(series.dataItems);
}

// function formatText(item, index) {
//   let formattedValue = item.value.toLocaleString("es-CL", {
//     minimumFractionDigits: 1,
//     maximumFractionDigits: 1,
//   });
//   console.log(item)
//   console.log("hola mundo")
//   if (item.currency !== "USD" && formattedValue.endsWith(",0")) {
//     formattedValue = formattedValue.slice(0, -2);
//   }

//   if (item.dolar) {
//     return `USD $${item.dolar.toLocaleString("es-CL", {
//       minimumFractionDigits: 2,
//       maximumFractionDigits: 2,
//     })}  -   $${formattedValue}`;
//   }

//   if (item.euro) {
//     return `EUR €${item.euro.toLocaleString("es-CL", {
//       minimumFractionDigits: 2,
//       maximumFractionDigits: 2,
//     })}  -   $${formattedValue}`;
//   }

//   return `$${formattedValue}`;
// }

onMounted(async () => {
  if (chartDiv.value) {
    const success = await loadAmcharts();
    if (!success) return;

    createRoot(chartDiv.value);
    chart = createChart();
    createSeries(chart);
    if (props.legend) addLegend(chart, series);
    if (props.animated) addAnimation(series);
  }
});

watch(
  () => props.data,
  () => {
    if (series) {
      updateSeriesData();
      // legend.data.setAll(series.dataItems);
    }
  },
  { deep: true },
);

onBeforeUnmount(() => {
  if (root) root.dispose();
});
</script>
